import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useProgram } from '@/hooks/useProgram'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import {
	Loader2,
	Check,
	ChevronsUpDown,
	X,
	Tag as TagIcon,
	Lightbulb,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'

interface Tag {
	id: string
	name: string
	color: string | null
	program_id: string
}

interface CreateFeatureModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (data: {
		title: string
		description: string
		category_id?: string | null
		tag_ids?: string[]
	}) => Promise<void>
	initialStatus: string
}

const getStatusColorClasses = (status: string) => {
	switch (status?.toLowerCase()) {
		case 'new':
			return 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700'
		case 'under consideration':
			return 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/50 dark:text-purple-300 dark:border-purple-700'
		case 'planned':
			return 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-700'
		case 'in progress':
			return 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700'
		case 'launched':
			return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700'
		case 'declined':
			return 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/50 dark:text-rose-300 dark:border-rose-700'
		default:
			return 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600'
	}
}

export function CreateFeatureModal({
	isOpen,
	onClose,
	onSubmit,
	initialStatus,
}: CreateFeatureModalProps) {
	const { t } = useTranslation()
	const [title, setTitle] = useState('')
	const [description, setDescription] = useState('')
	const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set())
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isTagPopoverOpen, setIsTagPopoverOpen] = useState(false)
	const { toast } = useToast()
	const { programId } = useProgram()

	// Fetch available tags for the current program
	const { data: allTags = [], isLoading: isLoadingTags } = useQuery<Tag[]>({
		queryKey: ['tags', programId],
		queryFn: () => apiClient(`/api/tags?programId=${programId}`),
		enabled: isOpen && !!programId,
	})

	const handleSubmit = async () => {
		if (!title.trim() || !description.trim()) return
		setIsSubmitting(true)
		try {
			await onSubmit({
				title,
				description,
				category_id: null,
				tag_ids: Array.from(selectedTagIds),
			})

			// Reset form
			setTitle('')
			setDescription('')
			setSelectedTagIds(new Set())

			toast({
				title: t('createFeatureModal.featureCreatedSuccess'),
				description: t('createFeatureModal.featureAddedToRoadmap', { title }),
			})
			onClose()
		} catch (error: any) {
			toast({
				title: t('createFeatureModal.errorCreatingFeature'),
				description: error.message || t('createFeatureModal.failedToCreateFeature'),
				variant: 'destructive',
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleClose = () => {
		if (!isSubmitting) {
			setTitle('')
			setDescription('')
			setSelectedTagIds(new Set())
			onClose()
		}
	}

	const handleTagToggle = (tagId: string) => {
		setSelectedTagIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(tagId)) {
				newSet.delete(tagId)
			} else {
				newSet.add(tagId)
			}
			return newSet
		})
	}

	const removeTag = (tagId: string) => {
		setSelectedTagIds((prev) => {
			const newSet = new Set(prev)
			newSet.delete(tagId)
			return newSet
		})
	}

	const selectedTags = allTags.filter((tag) => selectedTagIds.has(tag.id))

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
				<DialogHeader className="space-y-3 pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-primary/10 rounded-lg">
							<Lightbulb className="h-5 w-5 text-primary" />
						</div>
						<div className="flex-1">
							<DialogTitle className="text-xl font-semibold">
								{t('createFeatureModal.title')}
							</DialogTitle>
							<DialogDescription className="mt-1">
								{t('createFeatureModal.description')}
							</DialogDescription>
						</div>
					</div>

					<div className="flex items-center gap-2 pt-2">
						<span className="text-sm text-muted-foreground">{t('createFeatureModal.status')}</span>
						<Badge
							variant="outline"
							className={cn(
								'font-medium',
								getStatusColorClasses(initialStatus)
							)}>
							{initialStatus}
						</Badge>
					</div>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* Title Input */}
					<div className="space-y-2">
						<Label htmlFor="title" className="text-sm font-medium">
							{t('createFeatureModal.titleLabel')} <span className="text-destructive">*</span>
						</Label>
						<Input
							id="title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={t('createFeatureModal.titlePlaceholder')}
							disabled={isSubmitting}
							className="h-11"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="description" className="text-sm font-medium">
							{t('createFeatureModal.descriptionLabel')} <span className="text-destructive">*</span>
						</Label>
						<Textarea
							id="description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder={t('createFeatureModal.descriptionPlaceholder')}
							disabled={isSubmitting}
							className="min-h-[100px] resize-none"
							rows={4}
						/>
						<p className="text-xs text-muted-foreground">
							{t('createFeatureModal.descriptionHint')}
						</p>
					</div>

					{/* Tags Selection */}
					<div className="space-y-3">
						<div className="flex items-center gap-2">
							<TagIcon className="h-4 w-4 text-muted-foreground" />
							<Label className="text-sm font-medium">{t('createFeatureModal.tags')}</Label>
							<span className="text-xs text-muted-foreground">{t('createFeatureModal.optional')}</span>
						</div>

						{/* Selected Tags Display */}
						{selectedTags.length > 0 && (
							<div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg border">
								{selectedTags.map((tag) => (
									<Badge
										key={tag.id}
										variant="secondary"
										className="flex items-center gap-1 px-2 py-1"
										style={{
											backgroundColor: tag.color ? `${tag.color}20` : undefined,
											borderColor: tag.color || undefined,
											color: tag.color || undefined,
										}}>
										{tag.name}
										<X
											className="h-3 w-3 cursor-pointer hover:text-destructive"
											onClick={() => removeTag(tag.id)}
										/>
									</Badge>
								))}
							</div>
						)}

						{/* Tag Selector */}
						<Popover open={isTagPopoverOpen} onOpenChange={setIsTagPopoverOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={isTagPopoverOpen}
									className="w-full justify-between h-11"
									disabled={isSubmitting || isLoadingTags}>
									{isLoadingTags ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t('createFeatureModal.loadingTags')}
										</>
									) : selectedTagIds.size > 0 ? (
										selectedTagIds.size > 1 
											? t('createFeatureModal.tagsSelectedPlural', { count: selectedTagIds.size })
											: t('createFeatureModal.tagsSelected', { count: selectedTagIds.size })
									) : (
										t('createFeatureModal.selectTags')
									)}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className="w-[--radix-popover-trigger-width] p-0"
								align="start">
								<Command>
									<CommandInput placeholder={t('createFeatureModal.searchTags')} />
									<CommandList>
										<CommandEmpty>
											{isLoadingTags ? t('createFeatureModal.loadingTags') : t('createFeatureModal.noTagsFound')}
										</CommandEmpty>
										<CommandGroup>
											{allTags.map((tag) => (
												<CommandItem
													key={tag.id}
													value={tag.name}
													onSelect={() => handleTagToggle(tag.id)}
													className="flex items-center gap-2">
													<div
														className={cn(
															'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
															selectedTagIds.has(tag.id)
																? 'bg-primary text-primary-foreground'
																: 'opacity-50 [&_svg]:invisible'
														)}>
														<Check className="h-4 w-4" />
													</div>
													<div
														className="w-3 h-3 rounded-full mr-2"
														style={{
															backgroundColor: tag.color || '#6B7280',
														}}
													/>
													<span>{tag.name}</span>
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					</div>
				</div>

				<DialogFooter className="border-t pt-4 gap-2">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={isSubmitting}
						className="min-w-[80px]">
						{t('createFeatureModal.cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isSubmitting || !title.trim() || !description.trim()}
						className="min-w-[120px]">
						{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t('createFeatureModal.createFeature')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
